
import OcSliderButtons from '@marketplace/components/shared/container/slider-parts/oc-slider-buttons.vue'
import {
    DEFAULT_TILE_SIZES,
    OC_SLIDER_PROPS,
} from '@marketplace/components/shared/container/slider-parts/oc-slider-config.js'
import OcSliderTrackingMixin from '@marketplace/components/shared/container/slider-parts/oc-slider-tracking.js'

export default {
    name: 'OcSlider',

    components: {
        OcSliderButtons,
    },

    mixins: [OcSliderTrackingMixin],

    props: {
        ...OC_SLIDER_PROPS,
        tracking: {
            type: Object,
            default: () => {},
        },
        imagesCount: {
            type: Number,
            default: 0,
        },
        fixedDisplayMode: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            refID: 0,
            slideWidth: 0,
            slideCount: 0,
            scrollIndex: this.currentSlide,
            slidesSeenOnMobile: 0,
        }
    },

    computed: {
        displayedItemsPerSlide() {
            if (!this.decimalSlidesNumber) {
                if (this.$breakpoint.includes('md')) {
                    return DEFAULT_TILE_SIZES[this.sliderType].md
                }
                if (this.$breakpoint.includes('sm')) {
                    return DEFAULT_TILE_SIZES[this.sliderType].sm
                }
                if (this.$breakpoint.includes('xs')) {
                    return DEFAULT_TILE_SIZES[this.sliderType].xs
                }
            }
            return this.itemsPerSlide
        },
        fullSlidesCount() {
            return Math.floor(this.itemsPerSlide)
        },
        decimalSlidesNumber() {
            return this.itemsPerSlide - this.fullSlidesCount
        },
        singleSlidePercentage() {
            const width = `${(100 / this.displayedItemsPerSlide).toFixed(2)}%`

            const finalWidth = this.fixedSlideWidth
                ? `${this.fixedSlideWidth}px`
                : width

            if (this.fixedDisplayMode) {
                return {
                    'max-width': finalWidth,
                    display: 'inline-flex',
                }
            }

            return {
                flex: `0 0 ${finalWidth}`,
                width: finalWidth,
                'max-width': finalWidth,
            }
        },
        slidePosition() {
            const isLastSlide =
                this.scrollIndex + this.fullSlidesCount === this.slideCount
            const lastSlideWidth = this.slideWidth * this.decimalSlidesNumber
            let scrollAmount = this.slideWidth * this.scrollIndex

            if (isLastSlide && this.decimalSlidesNumber) {
                scrollAmount -= lastSlideWidth
            }

            if (this.fixedSlideWidth) {
                scrollAmount = this.fixedSlideWidth * this.scrollIndex
            }

            return this.canHaveDesktopSlider
                ? { transform: `translateX(-${scrollAmount}px)` }
                : false
        },
        visibleSlidesCount() {
            return this.slideCount - this.fullSlidesCount
        },
        canHaveDesktopSlider() {
            return (
                (!this.mobileSwipingActive ||
                    !this.$breakpoint?.includes('touch')) &&
                this.desktopSliderActive
            )
        },
        displaySliderButtons() {
            if (this.$breakpoint?.includes('touch')) {
                return !this.mobileSwipingActive
            }
            return true
        },
        disableLeftNav() {
            return this.scrollIndex <= 0
        },
        disableRightNav() {
            return this.scrollIndex >= this.visibleSlidesCount
        },
        hasEnoughToSlide() {
            return this.visibleSlidesCount > 0
        },
        arrowStyle() {
            return this.arrowsOffset !== 0
                ? { top: `${this.arrowsOffset}px` }
                : {}
        },
        arrowVisibility() {
            return this.desktopSliderActive && this.hasEnoughToSlide
        },
    },

    watch: {
        currentSlide() {
            this.scrollIndex = this.currentSlide
        },
        refreshSlider() {
            this.initSlider()
        },
        $breakpoint() {
            if (
                !this.mobileSwipingActive ||
                this.$breakpoint.includes('desktop')
            ) {
                this.reinitOnScreenSizeChange()
            }
        },
    },

    created() {
        this.refID = new Date().getTime()
    },

    mounted() {
        this.initSlider()
        if (this.$breakpoint.includes('mobile')) {
            this.$refs[`currentSlider-${this.refID}`].addEventListener(
                'scroll',
                this.onMobileSlide
            )
        }
    },

    beforeDestroy() {
        this.$refs[`currentSlider-${this.refID}`].removeEventListener(
            'scroll',
            this.onMobileSlide
        )
    },

    methods: {
        initSlider() {
            const slider = this.$refs[`currentSlider-${this.refID}`]
            if (slider?.firstElementChild) {
                let firstSlideWidth =
                    slider.firstElementChild.getBoundingClientRect().width

                // If we're using flex gap in fixedDisplayMode, add the columnGap to each slide's width
                if (this.fixedDisplayMode) {
                    const parentStyles = window.getComputedStyle(slider)
                    const colGap = parseFloat(parentStyles.columnGap) || 0
                    // Add that gap to the measured width
                    firstSlideWidth += colGap
                }

                this.slideWidth = firstSlideWidth
                this.slideCount = this.imagesCount || slider.children.length
            }
        },
        moveSlide(direction) {
            if (direction === '+') {
                this.$emit('arrowRight')
                this.trackSliderArrow(this.tracking, 'Right')
                if (this.scrollIndex + 1 <= this.visibleSlidesCount) {
                    this.scrollIndex += 1
                }
            } else if (direction === '-') {
                this.$emit('arrowLeft')
                this.trackSliderArrow(this.tracking, 'Left')
                if (this.scrollIndex - 1 < 0) {
                    this.scrollIndex = 0
                } else {
                    this.scrollIndex -= 1
                }
            }
            this.$emit('arrowCallback', this.scrollIndex)
        },
        reinitOnScreenSizeChange() {
            setTimeout(() => {
                this.initSlider()
                if (this.$refs[`currentSlider-${this.refID}`]) {
                    this.$refs[`currentSlider-${this.refID}`].scrollLeft = 0
                }
                this.scrollIndex = 0
            }, 50)
        },
    },
}
